// noinspection SpellCheckingInspection

import CheckIcon from "@mui/icons-material/Check";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ExitIcon from "@mui/icons-material/TransitEnterexit";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import UndoIcon from "@mui/icons-material/Undo";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiDrawer from "@mui/material/Drawer";
import { green, lightBlue } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import { alpha, styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import EntityMember from "_shared/entity-members/entity-member";
import TeamRosterTable, {
  TEAM_LEADER_ROLE,
  TEAM_MEMBER_ROLE,
  TEAM_ROSTER_PERSON_COLUMNS,
  type TeamRosterAddAction,
  type TeamRosterRow,
} from "_shared/entity-members/_views/team-roster-table";
import { FeeScheduleTableCellText } from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import {
  proposalTeamMemberItemId,
  useRemovedProposalTeamMembers,
  type ProposalTeamRemovedMember,
} from "work/proposals/negotiation/proposal-team-removed-members";
import ProposalFieldTimelineDropdown, {
  type ProposalFieldTimelineRevertEntry,
} from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import {
  proposalCollectionItemRevertValue,
  proposalScalarFieldRevertValue,
} from "work/proposals/negotiation/proposal-collection-item-revert";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import ProposalAddedBadge from "work/proposals/_views/_shared/proposal-added-badge";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import useAddAllColleagues, {
  ADD_ALL_COLLEAGUES_LABEL,
} from "work/work-agreements/teams/vendor-team/_views/members/add-all-colleagues-action";
import {
  mayAddAllColleagues,
  rosterLegalEntityId,
} from "work/work-agreements/teams/vendor-team/_views/members/add-all-colleagues";
import ProposalInfoLink from "work/proposals/_views/_shared/proposal-info-link";
import ProposalRedlineSubtext from "work/proposals/_views/_shared/proposal-redline-subtext";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import { TextChangeCollection } from "work/proposals/redlining/__diff/text-change";
import DiffText from "work/proposals/redlining/__diff/_views/diff-text";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import VendorTeamMemberSelection from "work/work-agreements/teams/vendor-team/_views/members/vendor-team-member-selection";
import {
  paneIsCompact,
  workPaneNarrowerThan,
} from "work/_views/work-pane-layout";

const MainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  columnGap: theme.spacing(10),
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  paddingBottom: theme.spacing(3),
  rowGap: theme.spacing(3),
  width: "100%",
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    columnGap: theme.spacing(3),
  },
  [paneIsCompact(theme)]: {
    flexDirection: "column",
    paddingBottom: 0,
  },
}));
const ResolvedHelperText = styled("span")(({ theme }) => ({
  color: green[700],
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.4,
}));
const TeamMemberDetails = styled("span")(() => ({
  display: "flex",
  flexDirection: "column",
}));
const TeamMemberName = styled("span", {
  shouldForwardProp: (prop) => prop !== "added",
})<{ added: boolean }>(({ added, theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(0.75),
  display: "inline-flex",
  // The member name is a Link, which pins its own body1 font weight, so bolding the line alone
  // would leave the name unbolded.
  ...(added && {
    fontWeight: theme.typography.fontWeightBold,
    "& a": { fontWeight: "inherit" },
  }),
}));
// The five-state redline ladder a roster member's avatar carries: accepted resolution, resolved
// resolution, an open removal, an open addition, and no change at all. It survives the table
// conversion untouched — it is the roster's primary redline signal, and the Fee Schedule's per-cell
// green box says only one of its five things. See the component comment below.
const MemberAvatar = styled(IndividualAvatar, {
  shouldForwardProp: (prop) => !["removed", "isNewlyAdded", "added", "resolved", "accepted"].includes(prop.toString()),
})<{
  removed?: boolean;
  isNewlyAdded: boolean;
  added?: boolean;
  resolved?: boolean;
  accepted?: boolean;
}>(({ removed, isNewlyAdded, added, resolved, accepted, theme }) => ({
  backgroundColor: (function () {
    if ((isNewlyAdded || added || removed) && resolved && accepted) return green[500];
    if ((isNewlyAdded || added || removed) && resolved) return lightBlue[700];
    if (removed && !resolved) return theme.palette.error.main;
    if (added) return green[300];
    return theme.palette.grey[400];
  })(),
}));
const DefaultMemberAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => !["removed", "isNewlyAdded", "added", "resolved", "accepted"].includes(prop.toString()),
})<{
  removed?: boolean;
  isNewlyAdded: boolean;
  added?: boolean;
  resolved?: boolean;
  accepted?: boolean;
}>(({ removed, isNewlyAdded, added, resolved, accepted, theme }) => ({
  backgroundColor: (function () {
    if ((isNewlyAdded || added || removed) && resolved && accepted) return green[500];
    if ((isNewlyAdded || added || removed) && resolved) return lightBlue[700];
    if (removed && !resolved) return theme.palette.error.main;
    if (added) return green[300];
    return theme.palette.grey[400];
  })(),
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const RedlinedLink = styled(DiffText)(() => ({
  fontSize: "inherit",
  fontWeight: "inherit",
  lineHeight: "inherit",
}));
const RemovedMemberDiff = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isNewlyRemoved",
})<{ isNewlyRemoved: boolean }>(({ theme, isNewlyRemoved }) => ({
  color: theme.palette.text.primary,
  textDecoration: "line-through",
  textDecorationColor: isNewlyRemoved ? lightBlue[700] : theme.palette.common.black,
  textDecorationThickness: "2px",
  "&:hover": {
    textDecorationColor: (function () {
      let color = theme.palette.common.black;
      if (isNewlyRemoved) color = lightBlue[700];
      return `oklch(from ${color} l c h / 0.25)`;
    })(),
  },
}));
const selectionPanelWidth = "min(500px, 100vw)";
const Drawer = styled(MuiDrawer)(({ theme }) => ({
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    boxSizing: "border-box",
    height: "calc(100% - 64px)",
    maxWidth: "100vw",
    top: "64px",
    width: selectionPanelWidth,
    // The paper is positioned against the window, not against the pane, so how far down the app bar
    // ends is a question about the window — one of the few on these pages that still is.
    [theme.breakpoints.down("lg")]: {
      height: `calc(100% - ${theme.spacing(6)})`,
      top: theme.spacing(6),
    },
  },
}));
const SelectionPanel = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition",
})<{ open?: boolean }>(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
      duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
    }),
    width: open ? selectionPanelWidth : 0,
  },
}));
const SelectionPanelContainer = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  maxWidth: "100vw",
  minHeight: 0,
  width: selectionPanelWidth,
}));
const SelectionPanelTitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2, 1),
}));
const SelectionPanelContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
  minHeight: 0,
}));
const SelectionPanelActions = styled("div")(({ theme }) => ({
  backgroundColor: "#FAFAFA",
  padding: theme.spacing(1.5, 2, 10),
}));
const SelectionPanelCancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

type TeamRedlineSelectionProps = {
  className?: string;
  topControls?: React.ReactNode;
  activeReviewField?: ProposalField;
  teamLeader?: EntityMember;
  teamMembersRedline: FieldRedlineArray<EntityMember>;
  /**
   * Whether this team is restricted. Not a rule about who may join — it decides what the roster
   * costs, because a restricted team's every member must approve the proposal before it can be
   * hired. "Add all colleagues" says so before it adds anyone.
   */
  teamRestricted?: boolean;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  negotiationId?: string;
  viewerScope?: string;
  selectionPresentation?: "dialog" | "drawer";
  onSelectionDrawerOpenChange?: (isOpen: boolean) => void;
  onTeamMembersRedlineChange: (
    newTeamMembersRedline: FieldRedlineArray<EntityMember>,
    traversalFieldOverride?: ProposalField | null,
  ) => void;
  onMemberQuitTeam: (memberId: Guid) => void;
  onMemberClicked: (id: Guid) => void;
  /**
   * Called with the leader reverted to a prior value (parsed from that value's history entry). The
   * caller applies it through the leader's own setter (`Proposal.updateVendorTeamLeader`), so the
   * revert flows through the same `queueProposalUpdate` path and lands as a plain `/vendorTeam/leader`
   * `FieldSet` — never a raw whole-team overwrite that would also touch `members` — matching the way
   * Name/Description revert through their own setters. See `proposal-field-timeline-dropdown.tsx`'s
   * `scalarPath`: `/vendorTeam/leader` is a scalar field whose `ProposalField.TeamLeader(id)` carries an
   * id for comment/history scoping only, so it must not be routed through the collection-item mechanism
   * (that would derive an itemId matching no event and render a permanently empty menu — ROADMAP.md
   * item 3's recorded exclusion).
   */
  onTeamLeaderReverted?: (leader: EntityMember) => void;
};

/**
 * The vendor team's roster, drawn as ONE table — Name, Email, Company, Role — through the shared
 * `TeamRosterTable`, the way the Fee Schedule tab is one table (#4955).
 *
 * One table rather than two. The leader and a member do differ — `/vendorTeam/leader` is a scalar
 * field, a member is a collection item keyed by the compound `userId:legalEntityId`, and their
 * controls follow from that — but that difference is what the Role column states and what the row's
 * own controls show. Splitting the team into two tables to say it left the reader with two one-column
 * lists and no way to read the team as a team.
 *
 * The redline signal stays where it was. A member's avatar keeps its five-state colour ladder rather
 * than moving to the Fee Schedule's per-cell green box: a fee category is negotiated column by column,
 * so its resolution has to be drawn per cell, while a team member is negotiated whole — one accept or
 * reject decides the person. The row-level "decided and accepted" surround is drawn around the whole
 * row. The edit lock is a column of its own rather than a swapped-out avatar, so a locked roster still
 * shows that ladder.
 */
export default function TeamRedlineSelection(props: Readonly<TeamRedlineSelectionProps>) {
  const {
    className,
    topControls,
    activeReviewField,
    teamLeader,
    teamMembersRedline,
    teamRestricted,
    disableEditing,
    fieldLockTooltip,
    negotiationId,
    viewerScope,
    onSelectionDrawerOpenChange,
    onTeamMembersRedlineChange,
    onMemberQuitTeam,
    onMemberClicked,
    onTeamLeaderReverted,
  } = props;
  const selectionPresentation = props.selectionPresentation ?? "dialog";

  // #region Hooks
  const confirm = useConfirmDialog();
  const { openDialog, popDialog } = useDialog();
  const [searchParams] = useSearchParams();
  const session = useSession();
  const [selectionPanelOpen, setSelectionPanelOpen] = React.useState(false);
  // A lock column, not a lock avatar: the avatar carries the redline colour ladder, and replacing it
  // took the reviewer's primary signal away on exactly the rows they had come to read.
  const showLockColumn = Boolean(disableEditing && fieldLockTooltip);

  const closeSelectionPanel = useCallback(() => {
    setSelectionPanelOpen(false);
    onSelectionDrawerOpenChange?.(false);
  }, [onSelectionDrawerOpenChange]);

  const openSelectionPanel = useCallback(() => {
    setSelectionPanelOpen(true);
    onSelectionDrawerOpenChange?.(true);
  }, [onSelectionDrawerOpenChange]);

  useEffect(() => {
    return () => onSelectionDrawerOpenChange?.(false);
  }, [onSelectionDrawerOpenChange]);

  const originalTeamMemberUserIds: string[] = useMemo(() => {
    return teamMembersRedline.redlines
      .map((memberRedline) => memberRedline.originalEntry?.userId.value)
      .filter((id) => id !== undefined);
  }, [teamMembersRedline]);

  const revisedTeamMemberUserIds: string[] = useMemo(() => {
    return teamMembersRedline.redlines
      .map((memberRedline) => memberRedline.revisedEntry?.userId.value)
      .filter((id) => id !== undefined);
  }, [teamMembersRedline]);

  const currentTeamMemberUserIds: string[] = useMemo(() => {
    return teamMembersRedline.redlines
      .map((memberRedline) => memberRedline.currentEntry?.userId.value)
      .filter((id) => id !== undefined);
  }, [teamMembersRedline]);

  // #region Removed members
  // The members the team no longer has, kept as struck-through rows of their own so each keeps the
  // history of the member they were (#4954) — the whole-team history control that used to be the only
  // place they survived is gone. They are not part of `teamMembersRedline`: nothing here accepts,
  // rejects, undoes or removes them. A member the redline itself still holds as a removal (open for
  // undo, or awaiting a decision) is already a row, and is not repeated.
  const presentMemberItemIds = useMemo(
    () =>
      teamMembersRedline.redlines.flatMap((memberRedline) => {
        const itemId = proposalTeamMemberItemId(
          memberRedline.currentEntry ?? memberRedline.revisedEntry ?? memberRedline.originalEntry,
        );
        return itemId ? [itemId] : [];
      }),
    [teamMembersRedline],
  );
  const removedMembers = useRemovedProposalTeamMembers({ negotiationId, viewerScope, presentItemIds: presentMemberItemIds });
  const removedMemberUserIds: string[] = useMemo(
    () => removedMembers.map((removed) => removed.member.userId.value),
    [removedMembers],
  );
  // #endregion

  const allTeamMemberUserIds: string[] = useMemo(
    () =>
      [
        teamLeader?.userId.value,
        ...originalTeamMemberUserIds,
        ...currentTeamMemberUserIds,
        ...revisedTeamMemberUserIds,
        ...removedMemberUserIds,
      ].filter((id) => id !== undefined),
    [teamLeader, originalTeamMemberUserIds, currentTeamMemberUserIds, revisedTeamMemberUserIds, removedMemberUserIds],
  );

  const sortedUniqueTeamMemberUserIds: Set<string> = useMemo(() => {
    return new Set(allTeamMemberUserIds);
  }, [allTeamMemberUserIds]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    sortedUniqueTeamMemberUserIds.size ? { userIds: sortedUniqueTeamMemberUserIds } : skipToken,
  );

  const teamMemberIndividuals: Individual[] = useMemo(() => {
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const teamMemberMap: Map<string, Individual> = useMemo(() => {
    const map = new Map<string, Individual>();
    for (const individual of teamMemberIndividuals) {
      map.set(individual.userId.value, individual);
    }
    return map;
  }, [teamMemberIndividuals]);

  useEffect(() => {
    const target = document.getElementById(activeReviewField?.id?.value ?? "");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeReviewField]);

  /**
   * Checks if the user with the given id is already selected or invited to the vendor-team
   *
   * @param userId Id of the user to check
   * @returns True if the user is already selected or invited to the vendor-team, false otherwise
   */
  function getIsMemberAlreadyOnTeam(userId?: Guid): boolean {
    if (!userId) return false;

    const memberUserIds = teamMembersRedline?.redlines.map((memberRedline) =>
      memberRedline.currentEntry ? memberRedline.currentEntry.userId : undefined,
    );

    return (
      (memberUserIds?.some((memberUserId) => memberUserId?.isEqualTo(userId)) ?? false) ||
      (teamLeader?.userId?.isEqualTo(userId) ?? false)
    );
  }

  /**
   * Handles when a new member has been selected
   *
   * Declared ahead of the JSX that closes over it: the React Compiler cannot rewrite a reference to a
   * function that is only hoisted into scope, and skips the component when it finds one.
   *
   * @param newMember The new member to add to the vendor-team
   */
  function handleNewMemberSelected(newMember: Individual) {
    if (getIsMemberAlreadyOnTeam(newMember.userId)) {
      enqueueSnackbar("Selected user is already on vendor-team", {
        variant: "info",
        preventDuplicate: true,
        autoHideDuration: 7500,
      });
      return;
    }

    try {
      onTeamMembersRedlineChange(
        teamMembersRedline.addEntry(newMember.toEntityMember()),
        ProposalField.TeamMember(newMember.id),
      );
      if (selectionPresentation === "drawer") {
        closeSelectionPanel();
      }
    } catch (error) {
      console.error("Error adding new member to vendor-team", error);
      enqueueSnackbar("Error adding new member to vendor-team", {
        variant: "error",
      });
    }
  }

  function getSelectedTeamMemberUserIds(): Guid[] {
    const userIds: Guid[] =
      teamMembersRedline?.redlines
        .map((memberRedline) => memberRedline.currentEntry?.userId)
        .filter((id) => id != undefined) ?? [];
    if (teamLeader?.userId) {
      userIds.push(teamLeader.userId);
    }
    return userIds;
  }

  /**
   * Handles what happens when add member is clicked
   */
  function beginMemberSelection() {
    if (selectionPresentation === "drawer") {
      openSelectionPanel();
      return;
    }

    openDialog({
      title: "Select Member",
      component: (
        <VendorTeamMemberSelection
          onNewSelection={handleNewMemberSelected}
          selectLeader={false}
          selectMember={true}
          selectedTeamMemberUserIds={getSelectedTeamMemberUserIds()}
          searchParams={searchParams}
          closeDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }

  /**
   * Handles when a new member has been selected
   *
   * @param newMember The new member to add to the vendor-team
   */
  // A revert restores a member's prior value through the SAME `replaceEntryById` edit path any other
  // member change uses, so it records an ordinary redline "Edit" and flows to `queueProposalUpdate` as
  // a `CollectionItemUpdated`/`CollectionItemAdded` op — never a raw `FieldSet`. A vendor-team member's
  // canonical collection identity is the compound `userId:legalEntityId` (see
  // proposal-operation-identity.ts), not just the userId `ProposalField.TeamMember` carries, so the
  // dropdown is told that identity explicitly via `collectionItemId`.
  function handleMemberRevert(teamMemberRedline: FieldRedline<EntityMember>, entry: ProposalFieldTimelineRevertEntry) {
    if (!teamMemberRedline.field.id) return;
    // `entry.operation` is typed as the full content-operation union because `ProposalFieldTimelineDropdown`'s
    // `onRevert` is shared across scalar/collection-item/structured-scalar modes; this dropdown instance is
    // wired for collection-item mode (`collectionItemId` below), so it is always CollectionItemAdded/Updated
    // at runtime. Narrowing here is what lets `proposalCollectionItemRevertValue`'s parameter type stay
    // exactly those two kinds (never `FieldSet`) instead of the whole union.
    if (entry.operation?.kind !== "CollectionItemAdded" && entry.operation?.kind !== "CollectionItemUpdated") return;
    const revertValue = proposalCollectionItemRevertValue(entry.operation);
    if (revertValue === undefined) return;
    const revertedMember = EntityMember.fromJSON(revertValue as never);
    if (!revertedMember) return;
    try {
      onTeamMembersRedlineChange(teamMembersRedline.replaceEntryById(teamMemberRedline.field.id, revertedMember));
    } catch (error) {
      console.error("Error reverting vendor-team member", error);
      enqueueSnackbar("Error reverting vendor-team member", { variant: "error" });
    }
  }

  function memberCollectionItemId(member: EntityMember | null | undefined): string | undefined {
    if (!member?.userId || !member.legalEntityId) return undefined;
    return `${member.userId.value}:${member.legalEntityId.value}`;
  }

  // A revert restores the leader's prior value through the leader's own setter
  // (`onTeamLeaderReverted` → `Proposal.updateVendorTeamLeader`), so it flows to `queueProposalUpdate`
  // as a plain `/vendorTeam/leader` `FieldSet` — never a raw whole-team overwrite touching `members`.
  // `entry.operation` here is a `FieldSet` built by `buildProposalScalarFieldRevertOperation` (see
  // proposal-collection-item-revert.ts); only its `value` is used, the same way a member revert only
  // uses its collection operation's `value`.
  function handleLeaderRevert(entry: ProposalFieldTimelineRevertEntry) {
    // See the narrowing comment in `handleMemberRevert` above: this dropdown instance is wired for
    // structured-scalar mode (`scalarPath` below), so `entry.operation` is always `FieldSet` at runtime.
    if (entry.operation?.kind !== "FieldSet") return;
    const revertValue = proposalScalarFieldRevertValue(entry.operation);
    if (revertValue === undefined) return;
    const revertedLeader = EntityMember.fromJSON(revertValue as never);
    if (!revertedLeader) return;
    // Guarded here rather than with `?.` inside the try: the React Compiler cannot lower optional
    // chaining within a try/catch, and an absent callback was already a no-op.
    if (!onTeamLeaderReverted) return;
    try {
      onTeamLeaderReverted(revertedLeader);
    } catch (error) {
      console.error("Error reverting vendor-team leader", error);
      enqueueSnackbar("Error reverting vendor-team leader", { variant: "error" });
    }
  }

  function handleMemberRemoved(memberId: Guid) {
    try {
      onTeamMembersRedlineChange(teamMembersRedline.removeEntryByFieldId(memberId), null);
    } catch (error) {
      console.error("Error removing member from vendor-team", error);
      enqueueSnackbar("Error removing member from vendor-team", {
        variant: "error",
      });
    }
  }

  async function handleQuitTeam(memberId?: Guid) {
    if (!memberId) return;

    const response = await confirm({
      title: "Leave Proposal?",
      message: `Do you want to leave this proposal?`,
      okButtonText: "Leave",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      onMemberQuitTeam(memberId);
    } catch (error) {
      console.error("Error quitting vendor-team", error);
      enqueueSnackbar("Error quitting vendor-team", {
        variant: "error",
      });
    }
  }

  function openUserProfileDialog(individualId?: Guid) {
    if (!individualId) return;
    openDialog({
      component: <ViewIndividualProfile individualId={individualId} />,
      titleStyle: {
        paddingBottom: 0,
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  function handleUndoChanges(teamMemberRedline: FieldRedline<EntityMember>) {
    if (!teamMemberRedline.field.id) {
      console.error("Field ID is missing for vendor-team member redline");
      enqueueSnackbar("Error undoing changes", { variant: "error" });
      return;
    }
    // Hoisted out of the try block: the React Compiler cannot lower a ternary inside try/catch.
    const changedField = teamMemberRedline.isNewlyAdded ? null : teamMemberRedline.field;

    try {
      onTeamMembersRedlineChange(
        teamMembersRedline.undoRedlineById(teamMemberRedline.field.id),
        changedField,
      );
    } catch (error) {
      console.error("Error undoing changes", error);
      enqueueSnackbar("Error undoing changes", { variant: "error" });
    }
  }

  /**
   * Applies every chosen colleague in one change.
   *
   * A loop of single `onTeamMembersRedlineChange` calls would emit N redline arrays all derived from
   * the same captured `teamMembersRedline`, and the last one would win — so the reduction happens
   * here and the parent is told once. `null` rather than a member's field: the traversal target of
   * "eleven people at once" is the roster, not any one of them.
   */
  function handleColleaguesChosen(colleagues: EntityMember[]) {
    try {
      onTeamMembersRedlineChange(
        colleagues.reduce((redlines, colleague) => redlines.addEntry(colleague), teamMembersRedline),
        null,
      );
    } catch (error) {
      console.error("Error adding colleagues to vendor-team", error);
      enqueueSnackbar("Error adding colleagues to vendor-team", { variant: "error" });
    }
  }

  // Declared here rather than beside the roster below: a hook cannot be called conditionally, and it
  // has to come after the two functions it is handed, so that the React Compiler is not asked to lower
  // a reference to something declared further down the component (see app/CLAUDE.md).
  const addAllColleagues = useAddAllColleagues({
    alreadyOnTeamUserIds: getSelectedTeamMemberUserIds(),
    teamRestricted,
    onColleaguesChosen: handleColleaguesChosen,
  });

  const getActionButtonTooltipText = (isAdded: boolean, isRemoved: boolean, accept: boolean) => {
    const verb = accept ? "Accept" : "Reject";

    if (isAdded) return `${verb} New Member`;
    if (isRemoved) return `${verb} Removed Member`;
    return `${verb} All Changes`;
  };

  /**
   * Everything the roster's Name column should match a person on, across the spellings a redline holds
   * at once: search still finds someone by a name or an address the current revision struck out. The
   * same flattening the Fee Schedule does with `getTextRedlineSearchValue`.
   *
   * A search key only. The row's `sortText` is what the Name column is ordered by, because a string
   * that leads with a struck-out spelling would sort a renamed member under the name they no longer
   * have (#5164).
   */
  function rosterSearchText(individuals: readonly (Individual | undefined)[]): string {
    const words = individuals.flatMap((individual) => {
      if (!individual) return [];
      const email = individual.email?.value ?? "";
      return [individual.fullName, email];
    });
    return [...new Set(words.filter((word) => word.length > 0))].join(" ");
  }

  function teamMemberRosterRow(teamMemberRedline: FieldRedline<EntityMember>): TeamRosterRow | undefined {
    const fieldId = teamMemberRedline.field.id;
    if (!fieldId) {
      console.error("Field ID is missing for vendor-team member redline");
      return undefined;
    }

    const originalIndividual = teamMemberMap.get(teamMemberRedline.originalEntry?.userId.value ?? "");
    const revisedIndividual = teamMemberMap.get(teamMemberRedline.revisedEntry?.userId.value ?? "");
    const currentIndividual = teamMemberMap.get(teamMemberRedline.currentEntry?.userId.value ?? "");
    const displayedIndividual = currentIndividual ?? revisedIndividual ?? originalIndividual;

    // Hoisted out of the JSX below: the React Compiler cannot lower an optional chain that is itself
    // the test of a `??` (`fromRedlines(...)?.textChanges ?? []`) and skips the whole component.
    const memberLinkRedlines = TextChangeCollection.fromRedlines(
      {
        originalEntry: originalIndividual ?? null,
        revisedEntry: revisedIndividual ?? null,
        currentEntry: currentIndividual,
      },
      { diffWords: false },
    );
    const memberLinkTextChanges = memberLinkRedlines?.textChanges ?? [];
    const memberNameHasDiff = memberLinkTextChanges.some((change) => change.diff.added || change.diff.removed);

    const resolvedAccepted =
      teamMemberRedline.wasRevised &&
      teamMemberRedline.isResolved &&
      teamMemberRedline.isAccepted &&
      teamMemberRedline.canBeUndone;

    const memberItemId = memberCollectionItemId(
      teamMemberRedline.currentEntry ?? teamMemberRedline.revisedEntry ?? teamMemberRedline.originalEntry,
    );

    const avatar = (function () {
      if (teamMemberRedline.currentEntry) {
        return (
          <MemberAvatar
            individual={currentIndividual}
            added={teamMemberRedline.isAdded}
            isNewlyAdded={teamMemberRedline.isNewlyAdded}
            removed={teamMemberRedline.isRemoved}
            resolved={teamMemberRedline.isResolved}
            accepted={teamMemberRedline.isAccepted}
          />
        );
      }
      return (
        <DefaultMemberAvatar
          added={teamMemberRedline.isAdded}
          isNewlyAdded={teamMemberRedline.isNewlyAdded}
          removed={teamMemberRedline.isRemoved}
          resolved={teamMemberRedline.isResolved}
          accepted={teamMemberRedline.isAccepted}
        />
      );
    })();

    // The spelling the row is currently drawn under, as opposed to every spelling it is searchable
    // by: a member the team has just lost is named by the revision that still had them, everyone else
    // by the revision in force. It is what the Name column sorts on (#5164), and the `name` below is
    // built from the same value so the two cannot drift apart.
    const displayedName =
      teamMemberRedline.isRemoved && teamMemberRedline.isResolved
        ? (revisedIndividual?.fullName ?? originalIndividual?.fullName ?? "")
        : (displayedIndividual?.fullName ?? "");

    const name =
      teamMemberRedline.isRemoved && teamMemberRedline.isResolved ? (
        <RemovedMemberDiff isNewlyRemoved={teamMemberRedline.isNewlyRemoved}>
          {displayedName}
        </RemovedMemberDiff>
      ) : (
        <TeamMemberName added={teamMemberRedline.isAdded && teamMemberRedline.isResolved}>
          <ProposalInfoLink onClick={() => openUserProfileDialog(displayedIndividual?.id)}>
            {individualQuery.isLoading && "Loading..."}
            {!individualQuery.isLoading && individualQuery.isError && (
              <Typography color="error">Error loading</Typography>
            )}
            {!individualQuery.isLoading && individualQuery.isSuccess && !memberNameHasDiff && displayedName}
            {!individualQuery.isLoading && individualQuery.isSuccess && memberNameHasDiff && (
              <RedlinedLink
                textChanges={memberLinkTextChanges}
                allResolved={teamMemberRedline.isResolved}
              />
            )}
          </ProposalInfoLink>
          {teamMemberRedline.isAdded && teamMemberRedline.isResolved && (
            <ProposalAddedBadge>Added</ProposalAddedBadge>
          )}
        </TeamMemberName>
      );

    // The email has a column of its own now, so the caption under the name carries only the row's own
    // "this was decided" note.
    const detail = resolvedAccepted ? (
      <TeamMemberDetails>
        <ResolvedHelperText>Resolved</ResolvedHelperText>
      </TeamMemberDetails>
    ) : undefined;

    const redlinedAdded = teamMemberRedline.isAdded && teamMemberRedline.isResolved;
    const redlinedRemoved = teamMemberRedline.isRemoved && teamMemberRedline.isResolved;
    const redlinedIndividuals = [originalIndividual, revisedIndividual, currentIndividual];

    const actions = (
      <>
        {teamMemberRedline.currentEntry?.userId?.isEqualTo(session.user?.id) && (
          <Tooltip title="Quit Team">
            <span aria-label="Quit Team">
              <ActionButton onClick={() => handleQuitTeam(teamMemberRedline.currentEntry?.userId ?? undefined)}>
                <ExitIcon />
              </ActionButton>
            </span>
          </Tooltip>
        )}
        {!disableEditing && (
          <>
            {teamMemberRedline.isResolved && !teamMemberRedline.isRemoved && !teamMemberRedline.canBeUndone && (
              <Tooltip title="Remove Member">
                <span>
                  <ProposalDeleteActionButton
                    aria-label="Remove Member"
                    onClick={() => {
                      if (teamMemberRedline.currentEntry) {
                        handleMemberRemoved(teamMemberRedline.currentEntry?.id ?? undefined);
                      }
                    }}
                  />
                </span>
              </Tooltip>
            )}
            {teamMemberRedline.canBeUndone && (
              <Tooltip title="Undo Changes">
                <span aria-label="Undo Changes">
                  <ActionButton
                    onClick={() => {
                      try {
                        handleUndoChanges(teamMemberRedline);
                      } catch (error) {
                        console.error("Error undoing changes", error);
                        enqueueSnackbar("Error undoing changes", {
                          variant: "error",
                        });
                      }
                    }}
                  >
                    <UndoIcon color="secondary" />
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            {!teamMemberRedline.isResolved && (teamMemberRedline.isAdded || teamMemberRedline.isRemoved) && (
              <>
                <Tooltip title={getActionButtonTooltipText(teamMemberRedline.isAdded, teamMemberRedline.isRemoved, true)}>
                  <span
                    aria-label={getActionButtonTooltipText(
                      teamMemberRedline.isAdded,
                      teamMemberRedline.isRemoved,
                      true,
                    )}
                  >
                    <ActionButton
                      onClick={() => {
                        try {
                          onTeamMembersRedlineChange(teamMembersRedline.acceptRedlineById(fieldId));
                        } catch (error) {
                          console.error("Error accepting changes", error);
                          enqueueSnackbar("Error accepting changes", {
                            variant: "error",
                          });
                        }
                      }}
                    >
                      <CheckIcon color="success" />
                    </ActionButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={getActionButtonTooltipText(teamMemberRedline.isAdded, teamMemberRedline.isRemoved, false)}
                >
                  <span
                    aria-label={getActionButtonTooltipText(
                      teamMemberRedline.isAdded,
                      teamMemberRedline.isRemoved,
                      false,
                    )}
                  >
                    <ActionButton
                      onClick={() => {
                        try {
                          onTeamMembersRedlineChange(teamMembersRedline.rejectRedlineById(fieldId));
                        } catch (error) {
                          console.error("Error rejecting changes", error);
                          enqueueSnackbar("Error rejecting changes", {
                            variant: "error",
                          });
                        }
                      }}
                    >
                      <CloseIcon color="error" />
                    </ActionButton>
                  </span>
                </Tooltip>
              </>
            )}
          </>
        )}
        {disableEditing ? (
          <ProposalFieldHistoryButton
            field={teamMemberRedline.field}
            // The member's own history, not the whole roster's: a member's `ProposalField` carries
            // their user id, but their timeline path is the team's (#4954).
            collectionItemId={memberItemId}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
          />
        ) : (
          <ProposalFieldTimelineDropdown
            field={teamMemberRedline.field}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            collectionItemId={memberItemId}
            onRevert={(entry) => handleMemberRevert(teamMemberRedline, entry)}
          />
        )}
      </>
    );

    return {
      id: fieldId.value,
      searchText: rosterSearchText(redlinedIndividuals),
      sortText: displayedName,
      avatar,
      name,
      detail,
      email: {
        searchText: rosterEmailSearchText(redlinedIndividuals),
        sortText: displayedIndividual?.email?.value ?? "",
        content: redlinedCellText(displayedIndividual?.email?.value, redlinedAdded, redlinedRemoved),
      },
      company: {
        searchText: rosterCompanySearchText(redlinedIndividuals),
        sortText: displayedIndividual?.entityName ?? "",
        content: redlinedCellText(displayedIndividual?.entityName, redlinedAdded, redlinedRemoved),
      },
      role: { content: TEAM_MEMBER_ROLE },
      actions,
      isActive: activeReviewField?.id?.isEqualTo(fieldId) ?? false,
      isResolvedAccepted: resolvedAccepted,
      onClick: () => onMemberClicked(fieldId),
    };
  }

  /**
   * A member the team no longer has: struck through, with nothing to accept, reject, undo or remove,
   * and the history of the member they were — scoped to them — as the row's one control (#4954).
   */
  function removedTeamMemberRosterRow(removed: ProposalTeamRemovedMember): TeamRosterRow {
    const individual = teamMemberMap.get(removed.member.userId.value);
    const field = ProposalField.TeamMember(removed.member.userId);
    const loadingName = individualQuery.isLoading ? "Loading..." : "Former member";
    const displayName = individual?.fullName ?? loadingName;
    return {
      id: `removed-${removed.itemId}`,
      searchText: rosterSearchText([individual]) || displayName,
      // The search string appends the address to the name; the column sorts on the name alone (#5164).
      sortText: displayName,
      rowAttributes: { "data-removed-member": removed.itemId },
      avatar: individual ? (
        <MemberAvatar individual={individual} isNewlyAdded={false} removed resolved accepted={false} />
      ) : (
        <DefaultMemberAvatar isNewlyAdded={false} removed resolved accepted={false} />
      ),
      name: <RemovedMemberDiff isNewlyRemoved={false}>{displayName}</RemovedMemberDiff>,
      email: {
        searchText: rosterEmailSearchText([individual]),
        content: redlinedCellText(individual?.email?.value, false, true),
      },
      company: {
        searchText: rosterCompanySearchText([individual]),
        content: redlinedCellText(individual?.entityName, false, true),
      },
      role: { content: TEAM_MEMBER_ROLE },
      actions: (
        <ProposalFieldHistoryButton
          field={field}
          collectionItemId={removed.itemId}
          negotiationId={negotiationId}
          viewerScope={viewerScope}
        />
      ),
    };
  }

  function leaderRosterRows(): TeamRosterRow[] {
    if (!teamLeader) return [];
    const leaderIndividual = teamMemberMap.get(teamLeader.userId.value);
    return [
      {
        id: "vendor-team-leader",
        searchText: rosterSearchText([leaderIndividual]),
        sortText: leaderIndividual?.fullName ?? "",
        avatar: <IndividualAvatar individual={leaderIndividual} />,
        name: (
          <ProposalInfoLink
            sx={{
              opacity: individualQuery.isLoading ? 0.75 : 1,
              pointerEvents: individualQuery.isLoading ? "none" : "auto",
              textDecoration: individualQuery.isLoading ? "none" : "underline",
            }}
            onClick={() => openUserProfileDialog(leaderIndividual?.id ?? undefined)}
          >
            {individualQuery.isLoading ? "Loading..." : leaderIndividual?.fullName}
          </ProposalInfoLink>
        ),
        email: {
          searchText: rosterEmailSearchText([leaderIndividual]),
          content: leaderIndividual?.email?.value,
        },
        company: {
          searchText: rosterCompanySearchText([leaderIndividual]),
          content: leaderIndividual?.entityName,
        },
        role: { content: TEAM_LEADER_ROLE },
        // The leader is a scalar field (`/vendorTeam/leader`), so there is nothing here to accept,
        // reject, undo or remove — only the field's own history. See ROADMAP.md item 3.
        actions: disableEditing ? (
          <ProposalFieldHistoryButton
            field={ProposalField.TeamLeader(teamLeader.userId)}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
          />
        ) : (
          <ProposalFieldTimelineDropdown
            field={ProposalField.TeamLeader()}
            scalarPath="/vendorTeam/leader"
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            onRevert={handleLeaderRevert}
          />
        ),
      },
    ];
  }

  // The leader leads the roster, then the members, then the members the team no longer has. One table
  // in the order a reader would name the team.
  const rosterRows: TeamRosterRow[] = [
    ...leaderRosterRows(),
    ...teamMembersRedline.redlines.flatMap((memberRedline: FieldRedline<EntityMember>) => {
      const row = teamMemberRosterRow(memberRedline);
      return row ? [row] : [];
    }),
    ...removedMembers.map(removedTeamMemberRosterRow),
  ];

  // Whose roster this is, read off who is on it. A client may edit the vendor team too — the roster
  // is negotiable content — so without this the action would offer to put the client's own staff on
  // the firm's team.
  const showAddAllColleagues =
    !disableEditing &&
    mayAddAllColleagues(
      session.user?.legalEntityId,
      rosterLegalEntityId(
        teamLeader,
        teamMembersRedline.redlines.map((memberRedline) => memberRedline.currentEntry),
      ),
    );

  // The two ways this roster gains people. "Add Member" picks one from the directory and is the press
  // the split button itself makes; "Add All Colleagues" puts the firm's whole roster on the team and is
  // the alternative behind its dropdown — where it replaces the icon-only button that used to sit at
  // the right of the heading (#4955).
  const addActions: TeamRosterAddAction[] = disableEditing
    ? []
    : [
      { label: "Add Member", onClick: beginMemberSelection },
      ...(showAddAllColleagues
        ? [
          {
            label: ADD_ALL_COLLEAGUES_LABEL,
            icon: <GroupAddOutlinedIcon fontSize="small" />,
            loading: addAllColleagues.isWorking,
            onClick: addAllColleagues.addAllColleagues,
          },
        ]
        : []),
    ];

  return (
    <MainContainer className={className}>
      {topControls}
      <TeamContainer>
        <TeamRosterTable
          title="Team Roster"
          surface="active-vendor-team"
          columns={TEAM_ROSTER_PERSON_COLUMNS}
          rows={rosterRows}
          lockIndicator={showLockColumn ? <ReviewerEditLockIcon title={fieldLockTooltip} /> : undefined}
          emptyMessage="No Team Members"
          noMatchesMessage="No matching team members"
          enableSearch
          addActions={addActions}
        />
        {/* #4953: no roster-wide Accept All / Reject All / Undo All here. A member is decided on their
            own row, and the whole-proposal bulk actions on the active proposal page cover "settle
            everything at once" — a second, collection-scoped copy of them was clutter.
            #4954: no whole-team history here either — a member's history lives on their row, and a
            removed member keeps a row of their own.
            #4955: no whole-roster "Remove All Members" either. It was an icon-only trash beside the
            heading, which the owner's review asked to be taken off the team tables; removing a member
            is still offered on that member's own row, which is where a removal is decided. */}
      </TeamContainer>
      {selectionPresentation === "drawer" && (
        <SelectionPanel open={selectionPanelOpen} anchor="right" variant="persistent" closeAfterTransition={false}>
          <SelectionPanelContainer>
            <SelectionPanelTitleBar>
              <Typography variant="h5">Select Member</Typography>
              <Tooltip title="Minimize panel">
                <IconButton size="medium" onClick={closeSelectionPanel}>
                  <KeyboardTabIcon fontSize="large" />
                </IconButton>
              </Tooltip>
            </SelectionPanelTitleBar>
            <SelectionPanelContent>
              <VendorTeamMemberSelection
                onNewSelection={handleNewMemberSelected}
                selectLeader={false}
                selectMember={true}
                selectedTeamMemberUserIds={getSelectedTeamMemberUserIds()}
                searchParams={searchParams}
                closeDialog={closeSelectionPanel}
              />
            </SelectionPanelContent>
            <SelectionPanelActions>
              <SelectionPanelCancelButton fullWidth variant="outlined" color="primary" onClick={closeSelectionPanel}>
                Cancel
              </SelectionPanelCancelButton>
            </SelectionPanelActions>
          </SelectionPanelContainer>
        </SelectionPanel>
      )}
    </MainContainer>
  );
}

/**
 * Every address the roster's Email column should match, across the spellings a redline holds at once —
 * the same flattening `getTextRedlineSearchValue` does for a fee category, so a search still finds
 * someone by an address the current revision struck out.
 */
function rosterEmailSearchText(individuals: readonly (Individual | undefined)[]): string {
  return uniqueRosterWords(individuals.map((individual) => individual?.email?.value));
}

/**
 * Every firm name the roster's Company column should match. A reviewer or a member may sit at a
 * different firm from the leader, which is the whole reason the column is there.
 */
function rosterCompanySearchText(individuals: readonly (Individual | undefined)[]): string {
  return uniqueRosterWords(individuals.map((individual) => individual?.entityName));
}

function uniqueRosterWords(values: readonly (string | undefined)[]): string {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].join(" ");
}

/**
 * One redlined cell beyond the name: the Fee Schedule's body2 / ellipsis / measured-tooltip treatment
 * with the redline's own added and struck-through colouring over it. A value the viewer has none of —
 * an unpublished profile's email, a firm the directory would not name — is left undefined so the
 * roster draws its own empty mark, because the identifiers behind it are exactly what must not reach
 * the page (#4830).
 */
function redlinedCellText(value: string | undefined, added: boolean, removed: boolean): React.ReactNode {
  if (!value) return undefined;
  // The redline mark is the inner element and the cell treatment the outer one, so the words and the
  // strike-through that crosses them stay on the same node — the roster's own specs, and a reader
  // scanning for a struck-out address, both find the text where the decoration is.
  return (
    <FeeScheduleTableCellText title={value}>
      <ProposalRedlineSubtext added={added} removed={removed}>
        {value}
      </ProposalRedlineSubtext>
    </FeeScheduleTableCellText>
  );
}
